import { AvailabilityFilterModel } from "@/models/availability";
import Campaign, {
  Banner,
  CampaignFilters,
  ClientCampaignReport,
  LineItem,
} from "@/models/campaign";
import { CampaignReportData } from "@/models/CampaignReport";
import { Meta, QueryParams } from "@/models/general";
import api from "@/plugins/axios";

const CampaignsAPI = {
  async new(payload: Campaign): Promise<Campaign | undefined> {
    return api.post("/campaigns", payload);
  },
  async list(
    page = 1,
    pageSize = 10,
    filter?: CampaignFilters,
    sortBy?: string,
    order?: string
  ): Promise<{ data: Campaign[]; meta: Meta }> {
    const fields = {
      campaign: "id,ref,name,status,company,contact,user_id,start,end",
      company: "name",
      contact: "name",
    };

    return api.get("/campaigns", {
      params: {
        page: { number: page, size: pageSize },
        filter: {
          ...filter,
        },
        fields,
        sort: {
          key: sortBy,
          order: order,
        },
      },
    });
  },
  async all(params?: QueryParams): Promise<{ data: Campaign[]; meta: Meta }> {
    return api.get(`/campaigns/all`, { params: { ...params } });
  },
  async get(id: string): Promise<Campaign> {
    return api.get(`/campaigns/${id}`);
  },
  async update(payload: Campaign): Promise<Campaign> {
    return api.put(`/campaigns/${payload.id}`, payload);
  },
  async changeStatus(payload: Campaign): Promise<Campaign> {
    return api.put(`/campaigns/${payload.id}/status`, {
      status: payload.status,
    });
  },
  async getReport(
    campaingId: string,
    startDate?: string,
    endDate?: string
  ): Promise<CampaignReportData> {
    const params: { start_date?: string; end_date?: string } = {};
    if (startDate) params["start_date"] = startDate;
    if (endDate) params["end_date"] = endDate;
    return api.get(`/campaigns/${campaingId}/report`, { params });
  },
  async generateReport(
    payload: ClientCampaignReport
  ): Promise<ClientCampaignReport> {
    return api.post(`/client_campaign_reports`, payload);
  },
  async getPublicReport(
    campaign_id: string,
    token: string
  ): Promise<CampaignReportData> {
    return api.get(`/client_campaign_reports/${campaign_id}`, {
      params: { token: token },
    });
  },
  async duplicate(id: string): Promise<Campaign> {
    return api.post(`/campaigns/${id}/duplicate`);
  },
  async logs(id: string): Promise<any> {
    return api.get(`/campaigns/${id}/logs`);
  },
  lineItems: {
    async new(payload: LineItem, campaign_id: string): Promise<LineItem> {
      return api.post(`/campaigns/${campaign_id}/line_items`, payload);
    },
    async update(payload: LineItem, campaign_id: string): Promise<LineItem> {
      return api.put(
        `/campaigns/${campaign_id}/line_items/${payload.id}`,
        payload
      );
    },
    async get(id?: string, campaign_id?: string): Promise<LineItem> {
      return api.get(`/campaigns/${campaign_id}/line_items/${id}`);
    },
    async generate_line_items(payload: any): Promise<Campaign | LineItem[]> {
      return api.post("/campaigns/generate_line_items", payload);
    },
    async delete(id?: string, campaign_id?: string): Promise<void> {
      return api.delete(`/campaigns/${campaign_id}/line_items/${id}`);
    },
    async all(params: QueryParams): Promise<{ data: LineItem[]; meta: Meta }> {
      return api.get(`/line_items/all`, { params: params });
    },
    banners: {
      async get(campaign_id?: string, line_item_id?: string): Promise<Banner> {
        return api.get(
          `/campaigns/${campaign_id}/line_items/${line_item_id}/banner`
        );
      },
      async new(
        payload: Banner,
        campaign_id?: string,
        line_item_id?: string
      ): Promise<Banner> {
        return api.post(
          `/campaigns/${campaign_id}/line_items/${line_item_id}/banner`,
          payload
        );
      },
      async batch(payload: any, campaign_id?: string): Promise<any> {
        return api.post(`/campaigns/${campaign_id}/replace_banners`, payload);
      },
    },
    batch: {
      async update(lineItems: LineItem[], campaign_id: string): Promise<void> {
        return api.post(`/campaigns/${campaign_id}/line_items/batch/edit`, {
          line_items: lineItems,
        });
      },
      async delete(lineItems: LineItem[], campaign_id: string): Promise<void> {
        return api.post(`/campaigns/${campaign_id}/line_items/batch/delete`, {
          line_items: lineItems,
        });
      },
    },
  },
};

export default CampaignsAPI;
